
    // IMPORTS
    import { defineComponent, PropType, computed } from 'vue'

    import Indicator from './my-indicator.vue'

    import { DbConstants } from '@/const'
    import { MaterialListItem } from '@/types/material'
    import { buildClassificationLocalization, buildLocalization, buildUnitLocalization } from '@/util'

    export default defineComponent({
        name: 'stock-indicator',

        components: {
            Indicator
        },

        props: {
            materialItem: { type: Object as PropType<MaterialListItem>, required: true }
        },

        setup(props) {
            /********************************************************************************************************************
             INIT
             ********************************************************************************************************************/
            const { localize } = buildLocalization()
            const { localizeUnit3 } = buildUnitLocalization()
            const { localizePropValue } = buildClassificationLocalization()

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const isHidden = computed(() => props.materialItem.hiddenSparePart)

            const indicatorText = computed(() => {
                switch (props.materialItem.sparePartStatus) {
                    case DbConstants.sparePartStatusNames.activeInStock:
                        return (props.materialItem?.availableStock ?? 0) > 0
                            ? localize('inStock')
                            : localize('outOfStock')

                    case DbConstants.sparePartStatusNames.activeRefill:
                        return localize('nonStockItem')

                    case DbConstants.sparePartStatusNames.activeNoRefill:
                        return (props.materialItem?.availableStock ?? 0) > 0
                            ? localize('inStock')
                            : localizePropValue(
                                DbConstants.sparePartPropertyNames.sparePartStatus,
                                DbConstants.sparePartStatusNames.discontinuedNoReplacement)

                    case DbConstants.sparePartStatusNames.discontinuedNoReplacement:
                    case DbConstants.sparePartStatusNames.discontinuedWithReplacement:
                        return props.materialItem.statusText ?? props.materialItem.sparePartStatus

                    default:
                        return `Unexpected status: ${props.materialItem.sparePartStatus}`
                }
            })

            const indicatorClass = computed(() => {
                switch (props.materialItem.sparePartStatus) {
                    case DbConstants.sparePartStatusNames.activeInStock:
                        return (props.materialItem?.availableStock ?? 0) > 0
                            ? 'indicator-in-stock'
                            : 'indicator-out-of-stock'

                    case DbConstants.sparePartStatusNames.activeRefill:
                        return 'indicator-non-stock-item'

                    case DbConstants.sparePartStatusNames.activeNoRefill:
                        return (props.materialItem?.availableStock ?? 0) > 0
                            ? 'indicator-in-stock'
                            : 'indicator-discontinued'

                    case DbConstants.sparePartStatusNames.discontinuedNoReplacement:
                        return 'indicator-discontinued'

                    case DbConstants.sparePartStatusNames.discontinuedWithReplacement:
                        return 'indicator-replaced'

                    default:
                        return 'indicator-unexpected-status'

                }
            })

            const isInStock = computed(() => {
                switch (props.materialItem.sparePartStatus) {
                    case DbConstants.sparePartStatusNames.activeInStock:
                    case DbConstants.sparePartStatusNames.activeNoRefill:
                        return (props.materialItem?.availableStock ?? 0) > 0

                    default:
                        return false
                }
            })

            const unit = computed(() => localizeUnit3(props.materialItem.priceQtyUnit))

            const tooltip = computed(() => isInStock.value
                ? localize('atLeastOneInStock').replace('{0}', unit.value).replace('  ', ' ')
                : null)

            const computedData = { isHidden, indicatorText, indicatorClass, isInStock, tooltip }

            const publicMethods = { localize }

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...computedData, ...publicMethods }
        }
    });
